/* eslint-env browser */
import type { ThemeKey, DefaultThemeStyles } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, memo } from 'react';
import { flushSync } from 'react-dom';
import { classnames } from 'Utils/classnames/classnames.utils';
import { get, isUndefined, isFunction, noop, pick } from 'lodash';
import striptags from 'striptags';
import { ARROW_DOWN, ARROW_UP, ENTER } from 'Utils/keyCodes';
import { InputText } from 'Components/frameworks/form-inputs/input-text/InputText';
import type { IconName } from 'Components/frameworks/icon/Icon';
import 'Components/frameworks/icon/Icon';
import { DefaultLayout } from './InputDatalist.layouts';
import { themes } from './InputDatalist.themes';
export type DataListItem = {
  readonly id: string;
  readonly icon?: IconName;
  readonly label: string;
  readonly link?: string;
  readonly productType?: string;
  readonly subLabel1?: string;
  readonly subLabel2?: string;
  readonly type?: string;
};
export type SanitizedDataListItem<T extends DataListItem> = {
  item: T;
  label: string;
};
export type Props<T extends DataListItem> = {
  autoFocus?: boolean;
  className?: string;
  dataList?: ReadonlyArray<T>;
  hasValidationIcon?: boolean;
  hasClear?: boolean;
  iconLeft?: IconName;
  id?: string;
  isAnimated?: boolean;
  isDisabled?: boolean;
  isFilled?: boolean;
  isLoading?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  itemLayout?: (props: T) => ReactNode | null | undefined;
  itemTitle?: ReactNode;
  label?: string | ReactNode;
  name?: string;
  notes?: string;
  onBlur?: (arg0: React.SyntheticEvent) => void;
  onChange?: (name: string, value: string) => void;
  onClear?: (arg0: React.SyntheticEvent) => void;
  onClick?: (arg0: React.SyntheticEvent) => void;
  onClickOutside?: () => void;
  onFocus?: (arg0: React.SyntheticEvent) => void;
  onKeyDown?: (arg0: React.SyntheticEvent) => void;
  onKeyUp?: (arg0: React.SyntheticEvent) => void;
  onSelect?: (arg0: SanitizedDataListItem<T>) => void;
  onValidate?: () => void;
  placeholder?: string;
  theme?: ThemeKey<typeof themes>;
  validationState?: string;
  value?: string;
};

const defaultProps = {
  dataList: [],
  theme: 'default',
};

function InputDatalistInner<T extends DataListItem>(incomingProps: Props<T>) {
  const props = { ...defaultProps, ...incomingProps };
  const {
    className,
    dataList,
    id,
    isAnimated,
    isDisabled,
    isLoading,
    isReadOnly,
    itemLayout,
    itemTitle,
    theme,
    value,
    onClickOutside: onClickOutsideProp,
    onFocus: onFocusProp,
    onChange: onChangeProp,
    onClear: onClearProp,
    onKeyDown: onKeyDownProp,
    onSelect: onSelectProp = noop,
  } = props;

  const [index, setIndex] = useState(-1);
  const [isActive, setIsActive] = useState(false);
  const [selected, setSelected] = useState(value || '');

  const dataListElementRef = useRef<HTMLDivElement>(null);

  // Track isActive in a ref so the click-outside handler always reads the latest value
  // (mimics the class component's this.state.isActive access pattern)
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;

  // componentDidUpdate equivalent: sync selected from value prop when not active.
  // Original CDU: if (!isUndefined(value) && !prevState.isActive && prevState.selected !== value)
  // Uses useLayoutEffect (not useEffect) to match componentDidUpdate's synchronous timing
  // after DOM commit. This prevents extra render cycles that would change effect ordering
  // in parent components.
  // Uses hasMountedRef to skip the first render (CDU doesn't fire on mount).
  const hasMountedRef = useRef(false);
  const prevIsActiveRef = useRef(isActive);
  const prevSelectedRef = useRef(selected);
  useLayoutEffect(() => {
    if (hasMountedRef.current) {
      if (!isUndefined(value) && !prevIsActiveRef.current && prevSelectedRef.current !== value) {
        setSelected(value);
      }
    }
    hasMountedRef.current = true;
    prevIsActiveRef.current = isActive;
    prevSelectedRef.current = selected;
  });

  const handleClickOutside = useCallback(
    (e: Event) => {
      if (!dataListElementRef.current || !isActiveRef.current) {
        return;
      }

      const dataListNode = dataListElementRef.current;

      if (!dataListNode.contains(e.target as Node)) {
        setIsActive(false);

        if (onClickOutsideProp) {
          onClickOutsideProp();
        }
      }
    },
    [onClickOutsideProp],
  );

  // componentDidMount/componentWillUnmount: register global event listeners once
  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('focus', handleClickOutside);
    }

    return () => {
      if (typeof window !== 'undefined') {
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('focus', handleClickOutside);
      }
    };
  }, [handleClickOutside]);

  const openList = useCallback(() => {
    setIsActive((prev) => {
      if (!prev) {
        return true;
      }
      return prev;
    });
  }, []);

  const handleSelect = useCallback(
    (item: T) => {
      const sanitizedItem: SanitizedDataListItem<T> = {
        item,
        label: striptags(get(item, 'label')),
      };
      setIndex(-1);
      setIsActive(false);
      setSelected(sanitizedItem.label);
      onSelectProp(sanitizedItem);
    },
    [onSelectProp],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const currentDataList = dataList || [];

      if (e.keyCode === ARROW_DOWN && index < currentDataList.length - 1) {
        setIndex((prev) => prev + 1);
      } else if (e.keyCode === ARROW_UP && index > 0) {
        setIndex((prev) => prev - 1);
      } else if (e.keyCode === ENTER) {
        const item = currentDataList[index];
        setIsActive(false);

        if (item) {
          handleSelect(item);
        } else if (selected === '' && isFunction(onClearProp)) {
          onClearProp({} as React.SyntheticEvent);
        } else if (isFunction(props.onValidate)) {
          props.onValidate();
        }
      }

      if (isFunction(onKeyDownProp)) {
        onKeyDownProp(e);
      }
    },
    [dataList, index, selected, onClearProp, onKeyDownProp, props.onValidate, handleSelect],
  );

  const handleFocus = useCallback(
    (e: React.SyntheticEvent) => {
      if (isFunction(onFocusProp) && !isActive) {
        onFocusProp(e);
      }

      openList();
    },
    [onFocusProp, isActive, openList],
  );

  const handleChange = useCallback(
    (name: string, inputValue: string) => {
      setIsActive(true);
      setSelected(inputValue);

      if (onChangeProp) {
        onChangeProp(name, inputValue);
      }
    },
    [onChangeProp],
  );

  const handleClear = useCallback(
    (e: React.SyntheticEvent) => {
      const clearCallback = onClearProp || noop;
      // flushSync replicates the original class component's setState(state, callback)
      // pattern: commit state first, then call the callback. This ensures the CDU-equivalent
      // effect can detect the isActive transition across two separate renders when the
      // callback triggers external state changes (e.g. Redux dispatches).
      flushSync(() => {
        setIndex(-1);
        setIsActive(false);
        setSelected('');
      });
      clearCallback(e);
    },
    [onClearProp],
  );

  const handleClickItem = useCallback((item: T) => () => handleSelect(item), [handleSelect]);

  const { thDatalist, thRoot } = themes[theme] as DefaultThemeStyles<typeof themes>;

  return (
    <div className={classnames(thRoot, className)} ref={dataListElementRef}>
      <InputText
        {...pick(props, [
          'hasClear',
          'hasValidationIcon',
          'iconLeft',
          'isDisabled',
          'isFilled',
          'isLoading',
          'isReadOnly',
          'isRequired',
          'label',
          'name',
          'notes',
          'onBlur',
          'onClick',
          'onKeyUp',
          'placeholder',
          'validationState',
        ])}
        autoComplete="off"
        dataTestId="InputDatalistQuery"
        id={id}
        isActive={isActive && !isReadOnly}
        onChange={handleChange}
        onClear={handleClear}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        type="text"
        value={selected}
      />
      {isActive && !isReadOnly && !isDisabled && !isLoading && !!dataList.length && (
        <ul
          className={classnames(thDatalist, {
            'animation-duration-500 animate-fadeIn': isAnimated,
          })}
          data-testid="InputDatalistList"
          role="listbox"
        >
          {!!itemTitle && <li>{itemTitle}</li>}
          {dataList.map((item, mapIndex) => {
            if (!item.label) {
              return null;
            }

            const newItem = { ...item, isActive: mapIndex === index };
            const hasCustomLayout = isFunction(itemLayout);
            return (
              <li
                data-testid={item.id}
                key={item.id ? item.id : mapIndex}
                onClick={handleClickItem(item)}
              >
                {hasCustomLayout ? itemLayout(newItem) : <DefaultLayout {...newItem} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export const InputDatalist = memo(InputDatalistInner) as typeof InputDatalistInner;
